import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import {
    ARRIVAL_DATE_RANGE,
    DEPARTURE_DATE_RANGE,
    EQUAL_TO_OPERATOR,
    eventOperatorOptions,
} from '../helpers/constants';
import { commaSeparatedEmailValidator } from '../../../../vue-vee-validate';
import { integer, required } from '@vee-validate/rules';

export const triggerMixin = {
    inject: ['setInvalid'],
    methods: {
        getConditionLabelObj(condition, isPassive) {
            const sourceOptions = isPassive ? this.conditionIdentifiers : this.eventIdentifiers;
            const source = sourceOptions.find((c) => c.value === condition.source)?.text;
            const getOptions = isPassive ? this.getConditionsOptions : this.getEventOptions;
            let operator = getOptions(condition.source)?.operator?.find((c) => c?.value === condition.operator)?.text;
            let target = getOptions(condition.source)?.target?.find((c) => c?.value === condition.target)?.text;
            if (condition.source === 'RECURRING') {
                target = this.generateRecurringString(condition, this.userUnicodeDateFormat);
            }
            if (condition.source === ARRIVAL_DATE_RANGE || condition.source === DEPARTURE_DATE_RANGE) {
                target = this.generateDateRangeString(condition);
            }
            if (condition.source === 'DATE') {
                if (condition.date) {
                    target = format(parseISO(condition.date), this.userUnicodeDateFormat);
                }
            }

            return source
                ? {
                      source,
                      operator,
                      target,
                  }
                : undefined;
        },
        getConditionSourceOptions(isPassive = false, value) {
            const options = isPassive ? this.conditionIdentifiers : this.eventIdentifiers;
            const option = options.find((item) => item.value === value);
            if (option) {
                return {
                    operator: isPassive ? [EQUAL_TO_OPERATOR] : eventOperatorOptions[option.value],
                    target: isPassive
                        ? this.conditionOptions && this.conditionOptions[option.value]
                        : this.eventOptions && this.eventOptions[option.value],
                };
            }
            return {};
        },
        getConditionsOptions(value) {
            return this.getConditionSourceOptions(true, value);
        },
        getEventOptions(value) {
            return this.getConditionSourceOptions(false, value);
        },
        getNewSelectedCondition(item, field, value, isPassive) {
            if (field === 'source') {
                const isValid = this.validateCondition(field, value);
                const condition = {
                    [field]: isValid ? value : null,
                };
                if (!isValid) {
                    return condition;
                }
                if (value === 'RECURRING') {
                    condition.interval = 1;
                    condition.freq = 'DAILY';
                    condition.bymonth = null;
                    condition.bymonthday = [];
                    condition.byday = [];
                    condition.start = null;
                    condition.until = null;

                    return condition;
                }
                if (value === 'DATE') {
                    condition.date = '';
                }
                const options = this.getConditionSourceOptions(isPassive, value);

                const operator = options?.operator;
                if (!condition.operator && operator && operator.length) {
                    condition.operator = operator[0].value;
                }
                const target = options?.target;
                if (!condition.target && target && target.length) {
                    condition.target = target[0].value;
                }
                return condition;
            } else {
                const obj = {
                    ...item,
                    [field]: value,
                }
                if(['from_month', 'to_month'].includes(field)) {
                    if(field === 'from_month') {
                        obj.from_day = null;
                    }
                    if(field === 'to_month') {
                        obj.to_day = null;
                    }
                }
                return obj
            }
        },
        getHasRangeCondition(isArrival) {
            const source = isArrival ? ARRIVAL_DATE_RANGE : DEPARTURE_DATE_RANGE;
            const hasRangeCondition = this.conditions
                .filter((item) => {
                    if (this.isArray(item)) {
                        return !!item.find((i) => i.source === source);
                    }
                    return item.source === source;
                })
                .flat();
            return hasRangeCondition.length > 0;
        },
        validateCondition(field, value) {
            let valid = true;
            if (field == 'source') {
                if (
                    (value === DEPARTURE_DATE_RANGE && this.getHasRangeCondition(false)) ||
                    (value === ARRIVAL_DATE_RANGE && this.getHasRangeCondition(true))
                ) {
                    valid = false;
                    this.errorToast({
                        text: `You can't select more than one ${
                            value === DEPARTURE_DATE_RANGE ? 'departure' : 'arrival'
                        } date range condition per trigger`,
                    });
                }
            }

            return valid;
        },
        onSelectCondition(value, field, index, nestedIndex) {
            const conditions = this.conditions.map((item, i) => {
                if (index === i) {
                    if (this.isArray(item)) {
                        return item.map((p, i2) => {
                            if (i2 === nestedIndex) {
                                return this.getNewSelectedCondition(p, field, value, true);
                            }
                            return p;
                        });
                    } else {
                        return this.getNewSelectedCondition(item, field, value, true);
                    }
                }
                return item;
            });
            this.$store.commit('trigger/setConditions', conditions);
            this.$store.commit('trigger/setEditing', true);
        },
        onSelectEvent(value, field, index) {
            const events = this.events.map((item, i) => {
                if (index === i) {
                    return this.getNewSelectedCondition(item, field, value, false);
                }
                return item;
            });
            this.$store.commit('trigger/setEvents', events);
            this.$store.commit('trigger/setEditing', true);
        },
        getEventError(value, rules) {
            // validation rules have performance issues with nested fields, so we need to validate manually
            const errors = [];

            rules.split('|').forEach((rule) => {
                if (rule === 'required') {
                    return required(value) ? null : errors.push('This field is required.');
                } else if (rule === 'validInteger') {
                    return integer(value) ? null : errors.push('This field must be a valid integer.');
                } else if (rule === 'commaSeparatedEmail') {
                    return commaSeparatedEmailValidator(value)
                        ? null
                        : errors.push('This field should be an email or comma-separated emails.');
                }
            });
            this.setInvalid(!!errors.length);
            return errors.length > 0 ? errors[0] : null;
        },
    },
};
